/**
 * SVG → EditorDocument importer (Tier 2 Group F sub-piece #3, commit 3).
 *
 * Parses an SVG text payload via the browser's DOMParser and walks
 * the element tree, producing:
 *
 *  - an {@link EditorDocument} the editor can mount
 *  - per-vector-layer `VectorPath` data the canvas hook materializes
 *    into Fabric Path objects (positioned in scene coordinates)
 *  - per-raster-layer "fragment-to-rasterize" descriptors the caller
 *    feeds to `rasterizeSvgFragment` (kept out of the parser so this
 *    module stays DOM-free for unit testing)
 *  - a list of warnings describing which SVG features were flattened
 *    or rejected — surfaced to the user via a post-import toast
 *
 * ## Scope (v1)
 *
 * Native vector representation:
 *  - `<path>` — d-string → `VectorPath` via `parseSvgPathData`
 *  - `<rect>` / `<circle>` / `<ellipse>` / `<polygon>` / `<polyline>` /
 *    `<line>` — converted to a path-data string first
 *  - `<g>` — group LayerNode (transform baked into children)
 *
 * Flattened to raster:
 *  - `<text>` / `<tspan>` / `<textPath>` — text rendering
 *  - `<filter>` (any element referencing it)
 *  - `<mask>` / `<clipPath>` (any element referencing one)
 *  - `<linearGradient>` / `<radialGradient>` / `<pattern>` fills/strokes
 *  - `<use>` / `<symbol>` references
 *  - `<foreignObject>`
 *  - `<image>` — embedded or external (the rasterizer handles decoding)
 *  - Groups whose own attributes require flattening (filter / mask /
 *    clipPath / opacity-via-css that isn't propagable as LayerNode
 *    opacity)
 *
 * Rejected (parse-time error toast):
 *  - SVGs containing `<script>` — security concern. The rasterizer
 *    strips scripts before rendering, but at the parser level we
 *    refuse the file to make the intent explicit.
 *  - Malformed XML — DOMParser returns a document with a
 *    `<parsererror>` element which we detect and throw on.
 *
 * ## Transforms
 *
 * SVG transforms compose down the tree (`<g transform="translate(10)"><g
 * transform="rotate(45)">`...). The editor's `LayerNode` has no
 * per-layer transform field, so transforms are BAKED at parse time:
 * each visit accumulates the parent's matrix with the element's own
 * `transform` attr and applies the final matrix to the element's
 * geometry (vector path coordinates, raster bbox). Matches what
 * Photoshop / Affinity do on SVG import.
 *
 * ## Document dimensions
 *
 * Priority: `viewBox` (preferred — defines the coordinate system) →
 * `width`/`height` attrs (when no viewBox). When neither is present,
 * a 1000×1000 fallback is used (matches Inkscape's default new-doc
 * size); the user can resize via Image → Canvas Size after import.
 *
 * ## No DOM dependencies in the pure walker
 *
 * `buildEditorDocumentFromSvgDoc` accepts a pre-parsed `XMLDocument`
 * (which IS DOM-shape but is what DOMParser hands back regardless of
 * environment — happy-dom ships its XMLDocument). The bytes-in
 * orchestrator `parseSvgToDocument` does the `new DOMParser().parseFromString`
 * and is the only DOM-touching entry point — same architectural split
 * as `psd-import.ts::buildEditorDocument` vs `parsePsdToDocument`.
 */

import type {
  BlendMode as EditorBlendMode,
  EditorDocument,
  LayerNode,
  ShapeFillValue,
  VectorPath,
} from '../types';
import type { SvgNodeOrigin } from './svg-source-stash';
import { SVG_WALK_SKIP_TAGS } from './svg-common';
import {
  parseSvgPathData,
  SvgPathParseError,
  rectToPathData,
  circleToPathData,
  ellipseToPathData,
  lineToPathData,
  pointsToPathData,
} from './svg-path-parse';
import {
  extractGradientDefs,
  parseUrlRef,
  resolveFillRef,
  type ResolvedGradient,
} from './svg-gradient-parser';
import { parseSvgTextElement, type ParsedSvgTextLayer } from './svg-text-import';

/** Convenience alias — the gradient-id → parsed-gradient map used
 *  by the walker to resolve `url(#id)` fill refs without re-parsing
 *  the SVG's `<defs>` every visit. Type intentionally narrowed to
 *  the shape `svg-gradient-parser` exposes; the underlying
 *  `ParsedGradient` is private to that module. */
type GradientDefs = ReturnType<typeof extractGradientDefs>;

/** Hard cap on nested-group depth. Pathological SVGs with thousands
 *  of nested `<g>` elements would otherwise blow the JS call stack
 *  on the recursive walker. 256 levels is far beyond anything a
 *  human creates in a design tool (Illustrator's panel UI breaks
 *  past ~30 levels). Pre-PR review finding (R3#5). */
const MAX_SVG_NESTING_DEPTH = 256;

/** Hard cap on total layer count. Importing an SVG with 100,000
 *  `<rect>` elements would emit 100,000 LayerNodes — the editor's
 *  history snapshot system isn't designed for that scale and would
 *  hang the tab. 10,000 is generous (Photoshop files commonly cap
 *  at 8000); past it we refuse rather than degrade silently. Pre-PR
 *  review finding (R3#7). */
const MAX_SVG_LAYER_COUNT = 10_000;

/** Reason a node was flattened or skipped, grouped by category for
 *  the post-import toast (matches `PsdImportWarning['reason']` shape). */
export type SvgImportWarningReason =
  | 'text'
  | 'gradient-or-pattern'
  | 'filter'
  | 'mask'
  | 'clip-path'
  | 'use-or-symbol'
  | 'foreign-object'
  | 'image'
  | 'malformed-path'
  | 'unsupported-blend-mode'
  | 'unsupported-element';

export interface SvgImportWarning {
  /** Editor LayerNode id, or null if the node was skipped entirely. */
  layerId: string | null;
  /** Tag name of the SVG element + its id attribute if present. */
  nodeDescription: string;
  reason: SvgImportWarningReason;
  /** Free-form detail for the click-to-expand UI. */
  message?: string;
}

/** A "rasterize this fragment" descriptor. The caller serializes the
 *  referenced subtree of the original SVG document, hands it to
 *  `rasterizeSvgFragment`, and the resulting canvas becomes the
 *  layer's pixel content. We don't run the rasterizer here to keep
 *  the walker free of canvas2d dependencies (for tests). */
export interface SvgRasterDescriptor {
  /** SVG fragment (the element's own serialization). */
  fragment: string;
  /** Scene-space bounding box where the rasterized canvas should be
   *  positioned. */
  left: number;
  top: number;
  width: number;
  height: number;
  /** Transform matrix applied to the rasterized canvas. When the
   *  rasterizer renders the fragment, it renders at width × height
   *  in the original coordinate system; the canvas is then placed
   *  at (left, top). For non-axis-aligned transforms (rotation,
   *  skew), this is approximate — the rasterized canvas captures
   *  the bounding box, not the rotated geometry. v1 accepts this
   *  trade-off; full transform-aware rasterization is a follow-up. */
  approximateTransform: boolean;
}

export interface SvgImportResult {
  document: EditorDocument;
  /** Vector layers indexed by editor LayerNode.id. Each entry is the
   *  fully-baked `VectorPath` (scene coordinates, transform-applied
   *  anchors). */
  vectorPaths: Map<string, VectorPath>;
  /** Raster layers indexed by editor LayerNode.id. Each entry is the
   *  fragment + bbox the rasterizer needs. */
  rasterDescriptors: Map<string, SvgRasterDescriptor>;
  /** Text layers indexed by editor LayerNode.id. Each entry is a
   *  parsed `<text>` element ready for IText materialization by
   *  the caller (EditorPage → useFabricCanvas). Tier 2 Group F SVG
   *  round-trip polish (PR-2 phase D). `<text>` elements that
   *  couldn't be expressed natively (tspan / textPath / gradient
   *  fill / non-trivial transform) still rasterize and appear in
   *  `rasterDescriptors` instead. */
  textDescriptors: Map<string, ParsedSvgTextLayer>;
  /** Maps editor LayerNode.id → which SVG element it originated from.
   *  Populated during the walk so the export path (sub-piece #4)
   *  can locate the original XML element in the re-parsed stash and
   *  restore features the walker flattened (text, filters, gradients,
   *  etc.). Editor-created layers (paste, new layer, etc.) have no
   *  entry here. */
  layerOriginById: Record<string, SvgNodeOrigin>;
  warnings: SvgImportWarning[];
  originalWidth: number;
  originalHeight: number;
  /** Optional <defs> block text extracted from the root SVG, ready
   *  to be inlined into rasterizer fragment wrappers. SVG fragments
   *  that reference `url(#gradient1)` paint servers won't render
   *  unless the defs are in scope; the importer hands this through
   *  to `rasterizeSvgFragment` so gradient/filter-referenced layers
   *  rasterize correctly. */
  defsBlock: string;
  /** The original SVG text — preserved for the source-stash so the
   *  export path (sub-piece #4) can round-trip features flattened
   *  here. */
  svgSource: string;
}

// ---------- Errors ----------------------------------------------------------

/** Thrown when the SVG contains a `<script>` element. Refused at the
 *  parser level (the rasterizer also strips scripts, but parsing
 *  scripted SVGs in the first place feels wrong). */
export class SvgScriptError extends Error {
  constructor() {
    super(
      'SVGs containing <script> elements are not supported for security reasons. Remove the script tags from your source tool and try again.',
    );
    this.name = 'SvgScriptError';
  }
}

/** Thrown when DOMParser couldn't parse the input as XML (the
 *  parsed document contains a `<parsererror>` element). */
export class SvgMalformedError extends Error {
  constructor(public readonly parserErrorText: string) {
    super(`SVG file is malformed: ${parserErrorText}`);
    this.name = 'SvgMalformedError';
  }
}

/** Thrown when the SVG declares its own internal XML entities via
 *  `<!DOCTYPE svg [<!ENTITY ... >]>`. Modern browsers' DOMParser
 *  generally disables external-entity resolution and limits
 *  recursive expansion (defending against the classic
 *  "billion laughs" DoS), but the protection isn't universal across
 *  every browser/version. Refusing entity-declaring SVGs upfront is
 *  a cheap belt-and-suspenders defense — legitimate creative-tool
 *  exports (Illustrator, Inkscape, Figma) never include entity
 *  declarations. Pre-PR review finding (R3#4). */
export class SvgEntityDeclarationError extends Error {
  constructor() {
    super(
      'SVG files containing XML entity declarations (<!DOCTYPE svg [<!ENTITY ...>]>) are not supported. Re-export from your source tool without DTD entities and try again.',
    );
    this.name = 'SvgEntityDeclarationError';
  }
}

/** Thrown when the SVG exceeds the layer-count cap. Importing
 *  pathological SVGs with 100,000+ elements would hang the editor's
 *  history-snapshot machinery; refuse upfront with an actionable
 *  message. Pre-PR review finding (R3#7). */
export class SvgLayerCountError extends Error {
  constructor(
    public readonly attemptedCount: number,
    public readonly limit: number,
  ) {
    super(
      `SVG contains ${attemptedCount}+ layers; the editor caps imports at ${limit}. Flatten or group elements in your source tool before importing.`,
    );
    this.name = 'SvgLayerCountError';
  }
}

/** Thrown when the SVG's nesting depth exceeds the recursion cap.
 *  Defends against pathological hand-crafted SVGs that would blow
 *  the JS call stack. Pre-PR review finding (R3#5). */
export class SvgNestingDepthError extends Error {
  constructor(
    public readonly depth: number,
    public readonly limit: number,
  ) {
    super(
      `SVG nesting depth of ${depth} exceeds the editor's ${limit}-level limit. Simplify the document structure in your source tool first.`,
    );
    this.name = 'SvgNestingDepthError';
  }
}

// ---------- 2D transform matrices ------------------------------------------
//
// SVG matrix(a, b, c, d, e, f) maps points (x, y) →
//   (a*x + c*y + e, b*x + d*y + f)
// This is the same as the editor's existing Fabric matrix convention
// (Fabric uses the same 6-element column-major format), so the math
// is shared with `bakeVectorPath` in `vector-path.ts`.

/** 2D affine transform: [a, b, c, d, e, f] mapping (x, y) →
 *  (a·x + c·y + e, b·x + d·y + f). */
export type Mat2D = readonly [number, number, number, number, number, number];

const IDENTITY_MATRIX: Mat2D = [1, 0, 0, 1, 0, 0];

function multiplyMatrix(a: Mat2D, b: Mat2D): Mat2D {
  return [
    a[0] * b[0] + a[2] * b[1],
    a[1] * b[0] + a[3] * b[1],
    a[0] * b[2] + a[2] * b[3],
    a[1] * b[2] + a[3] * b[3],
    a[0] * b[4] + a[2] * b[5] + a[4],
    a[1] * b[4] + a[3] * b[5] + a[5],
  ];
}

function applyMatrixToPoint(m: Mat2D, x: number, y: number): { x: number; y: number } {
  return {
    x: m[0] * x + m[2] * y + m[4],
    y: m[1] * x + m[3] * y + m[5],
  };
}

/** Parse an SVG `transform` attribute. Supports the full grammar:
 *  matrix(...), translate(...), scale(...), rotate(...), skewX(...),
 *  skewY(...). Multiple transforms compose left-to-right per the SVG
 *  spec (`transform="translate(10) rotate(45)"` first translates
 *  then rotates, so points are rotated first then translated when
 *  the matrix is applied — matches multiply order). */
export function parseSvgTransform(attr: string | null): Mat2D {
  if (!attr || attr.trim() === '') return IDENTITY_MATRIX;
  // Match `name(args)` repeatedly.
  const regex = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;
  let result: Mat2D = IDENTITY_MATRIX;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(attr)) !== null) {
    const fn = match[1];
    const args = match[2]
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => s !== '')
      .map(Number)
      .filter((n) => !Number.isNaN(n));
    let next: Mat2D = IDENTITY_MATRIX;
    switch (fn) {
      case 'matrix':
        if (args.length === 6) {
          next = [args[0], args[1], args[2], args[3], args[4], args[5]];
        }
        break;
      case 'translate':
        // translate(tx) → translate(tx, 0); translate(tx, ty) → both.
        next = [1, 0, 0, 1, args[0] ?? 0, args[1] ?? 0];
        break;
      case 'scale':
        // scale(sx) → scale(sx, sx); scale(sx, sy) → both.
        next = [args[0] ?? 1, 0, 0, args[1] ?? args[0] ?? 1, 0, 0];
        break;
      case 'rotate': {
        // rotate(angle [, cx, cy]) — angle in degrees. With center,
        // it's translate(cx, cy) · rotate(angle) · translate(-cx, -cy).
        const rad = ((args[0] ?? 0) * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const rotation: Mat2D = [cos, sin, -sin, cos, 0, 0];
        if (args.length === 3) {
          const t1: Mat2D = [1, 0, 0, 1, args[1], args[2]];
          const tNeg: Mat2D = [1, 0, 0, 1, -args[1], -args[2]];
          next = multiplyMatrix(t1, multiplyMatrix(rotation, tNeg));
        } else {
          next = rotation;
        }
        break;
      }
      case 'skewX': {
        const rad = ((args[0] ?? 0) * Math.PI) / 180;
        next = [1, 0, Math.tan(rad), 1, 0, 0];
        break;
      }
      case 'skewY': {
        const rad = ((args[0] ?? 0) * Math.PI) / 180;
        next = [1, Math.tan(rad), 0, 1, 0, 0];
        break;
      }
    }
    result = multiplyMatrix(result, next);
  }
  return result;
}

/** Apply a 2D matrix to every anchor in a `VectorPath`, baking the
 *  transform into scene coordinates. Handle deltas (which are
 *  directional vectors, not points) get the rotation/scale portion
 *  of the matrix only (no translation). Mirrors `bakeVectorPath` in
 *  `vector-path.ts` but for SVG-derived paths (no pathOffset). */
export function applyMatrixToVectorPath(path: VectorPath, m: Mat2D): VectorPath {
  if (m === IDENTITY_MATRIX || isIdentityMatrix(m)) return path;
  return {
    ...path,
    subPaths: path.subPaths.map((sub) => ({
      ...sub,
      anchors: sub.anchors.map((a) => {
        const p = applyMatrixToPoint(m, a.x, a.y);
        const next = { ...a, x: p.x, y: p.y };
        const transformDelta = (dx: number, dy: number) => ({
          x: m[0] * dx + m[2] * dy,
          y: m[1] * dx + m[3] * dy,
        });
        if (a.inX !== undefined && a.inY !== undefined) {
          const d = transformDelta(a.inX, a.inY);
          next.inX = d.x;
          next.inY = d.y;
        }
        if (a.outX !== undefined && a.outY !== undefined) {
          const d = transformDelta(a.outX, a.outY);
          next.outX = d.x;
          next.outY = d.y;
        }
        return next;
      }),
    })),
  };
}

function isIdentityMatrix(m: Mat2D): boolean {
  return m[0] === 1 && m[1] === 0 && m[2] === 0 && m[3] === 1 && m[4] === 0 && m[5] === 0;
}

// ---------- Attribute / style helpers --------------------------------------

/** Map SVG `mix-blend-mode` style values (CSS Compositing names) to
 *  the editor's `BlendMode`. SVG uses the same vocabulary as CSS so
 *  the mapping is nearly 1:1. */
const SVG_BLEND_MODE_MAP: Record<string, EditorBlendMode> = {
  normal: 'source-over',
  multiply: 'multiply',
  screen: 'screen',
  overlay: 'overlay',
  darken: 'darken',
  lighten: 'lighten',
  'color-dodge': 'color-dodge',
  'color-burn': 'color-burn',
  'hard-light': 'hard-light',
  'soft-light': 'soft-light',
  difference: 'difference',
  exclusion: 'exclusion',
  hue: 'hue',
  saturation: 'saturation',
  color: 'color',
  luminosity: 'luminosity',
};

/** Parse a number-or-percentage opacity value. SVG opacity is
 *  conventionally 0–1, but `style="opacity:50%"` is valid CSS. */
function parseOpacity(value: string | null): number | undefined {
  if (value === null || value.trim() === '') return undefined;
  const trimmed = value.trim();
  if (trimmed.endsWith('%')) {
    const n = parseFloat(trimmed.slice(0, -1));
    if (Number.isNaN(n)) return undefined;
    return Math.max(0, Math.min(1, n / 100));
  }
  const n = parseFloat(trimmed);
  if (Number.isNaN(n)) return undefined;
  return Math.max(0, Math.min(1, n));
}

/** Read a style declaration value from a `style="..."` attribute
 *  (e.g. `style="opacity: 0.5; fill: red"` + property `opacity` →
 *  `'0.5'`). Returns null when not present. Naive but sufficient
 *  for the properties we care about — full CSS parsing would be
 *  overkill. */
function readStyleProp(style: string | null, prop: string): string | null {
  if (!style) return null;
  // Property names are case-insensitive in CSS but ag-psd/SVG
  // convention uses lowercase.
  const regex = new RegExp(`${prop}\\s*:\\s*([^;]+)`, 'i');
  const match = regex.exec(style);
  if (!match) return null;
  return match[1].trim();
}

/** Resolve an SVG element's effective opacity, blend mode, and
 *  display state by checking both attributes and inline styles
 *  (style wins per CSS spec — but for the attributes SVG itself
 *  ships, the attribute wins when both are present).
 *
 *  Accepts a gradient-def map so the "is this a paint-server ref?"
 *  check can distinguish RESOLVABLE gradient refs (no raster — the
 *  vector emission carries the gradient as a `ShapeFillValue`) from
 *  PATTERN / unresolvable refs (still raster — the editor's model
 *  has no pattern representation). Tier 2 Group F SVG round-trip
 *  polish (PR-2). */
function readPresentationAttrs(
  el: SVGElement,
  gradients: GradientDefs,
): {
  opacity?: number;
  blendMode?: EditorBlendMode;
  visible: boolean;
  /** True when the element references a feature requiring rasterization. */
  needsRaster: boolean;
} {
  const styleAttr = el.getAttribute('style');
  const opacityAttr = el.getAttribute('opacity');
  const opacityStyle = readStyleProp(styleAttr, 'opacity');
  const opacity = parseOpacity(opacityAttr) ?? parseOpacity(opacityStyle);

  const blendAttr = readStyleProp(styleAttr, 'mix-blend-mode');
  const blendMode = blendAttr ? SVG_BLEND_MODE_MAP[blendAttr] : undefined;

  const displayAttr = el.getAttribute('display') ?? readStyleProp(styleAttr, 'display');
  const visibilityAttr = el.getAttribute('visibility') ?? readStyleProp(styleAttr, 'visibility');
  const visible = displayAttr !== 'none' && visibilityAttr !== 'hidden';

  // Rasterization triggers: element references a filter, mask, or
  // clip-path; element's fill/stroke uses a paint-server reference
  // (`url(#...)`) that ISN'T a resolvable gradient (patterns, mesh,
  // unresolvable refs); element opts out of native scaling
  // (vector-effect) or non-default paint order — both render
  // differently as native vector vs. flattened raster, so flattening
  // is the safer default.
  //
  // Stroke gradient refs always rasterize because `VectorStroke.color`
  // is `string` only — the editor's stroke model has no
  // `ShapeFillValue` slot. Lifting that limitation would require
  // model surgery the PR-2 scope explicitly excludes.
  const filter = el.getAttribute('filter');
  const mask = el.getAttribute('mask');
  const clip = el.getAttribute('clip-path');
  const fill = el.getAttribute('fill') ?? readStyleProp(styleAttr, 'fill');
  const stroke = el.getAttribute('stroke') ?? readStyleProp(styleAttr, 'stroke');
  const fillRefs = !!(fill && /^url\s*\(/i.test(fill));
  const strokeRefs = !!(stroke && /^url\s*\(/i.test(stroke));
  const fillResolvesNatively = fillRefs && canResolveFillRef(fill, gradients);
  const vectorEffect =
    el.getAttribute('vector-effect') ?? readStyleProp(styleAttr, 'vector-effect') ?? null;
  const paintOrder =
    el.getAttribute('paint-order') ?? readStyleProp(styleAttr, 'paint-order') ?? null;
  const hasNonDefaultVectorEffect = !!(vectorEffect && vectorEffect.trim() !== 'none');
  const hasNonDefaultPaintOrder = !!paintOrder;
  const needsRaster = !!(
    filter ||
    mask ||
    clip ||
    (fillRefs && !fillResolvesNatively) ||
    strokeRefs ||
    hasNonDefaultVectorEffect ||
    hasNonDefaultPaintOrder
  );

  return { opacity, blendMode, visible, needsRaster };
}

/** True when a `url(#id)` fill attribute resolves to a gradient
 *  the editor's `ShapeFillValue` model can represent. Used by the
 *  presentation-attr reader to suppress the rasterization trigger
 *  for resolvable gradient refs.
 *
 *  Defensive: a non-`url(#id)` string (named color, `none`, hex)
 *  returns false here too — the caller should have detected
 *  `fillRefs` before invoking us. */
function canResolveFillRef(fill: string | null, gradients: GradientDefs): boolean {
  if (!fill) return false;
  const id = parseUrlRef(fill);
  if (!id) return false;
  return resolveFillRef(id, gradients) !== null;
}

// ---------- Stroke / fill mapping -------------------------------------------

/** Parse a stroke specification from element attributes / style.
 *  Returns undefined when the element has no stroke (`stroke="none"`
 *  or missing). */
function readStroke(el: SVGElement): VectorPath['stroke'] | undefined {
  const styleAttr = el.getAttribute('style');
  const stroke = el.getAttribute('stroke') ?? readStyleProp(styleAttr, 'stroke');
  if (!stroke || stroke === 'none' || /^url\s*\(/i.test(stroke)) return undefined;
  const widthStr =
    el.getAttribute('stroke-width') ?? readStyleProp(styleAttr, 'stroke-width') ?? '1';
  const cap = (el.getAttribute('stroke-linecap') ?? readStyleProp(styleAttr, 'stroke-linecap')) as
    | 'butt'
    | 'round'
    | 'square'
    | null;
  const join = (el.getAttribute('stroke-linejoin') ??
    readStyleProp(styleAttr, 'stroke-linejoin')) as 'miter' | 'round' | 'bevel' | null;
  return {
    color: stroke,
    width: parseFloat(widthStr) || 1,
    cap: cap ?? undefined,
    join: join ?? undefined,
  };
}

/** Parse a fill specification from element attributes / style.
 *  Returns undefined when the element has no paintable fill
 *  (`fill="none"` or unresolvable `url(#id)` paint-server ref).
 *  SVG default is `fill="black"` when no fill attribute is set.
 *
 *  Accepts a gradient-def map so resolvable `url(#id)` refs return
 *  a `ShapeFillValue` instead of being dropped. Pattern / mesh /
 *  unresolvable refs still return undefined — the walker's needs-
 *  raster gate handles those by routing the element to the
 *  rasterizer. Tier 2 Group F SVG round-trip polish (PR-2). */
function readFill(el: SVGElement, gradients: GradientDefs): string | ShapeFillValue | undefined {
  const styleAttr = el.getAttribute('style');
  const fill = el.getAttribute('fill') ?? readStyleProp(styleAttr, 'fill');
  if (fill === null) return '#000000'; // SVG default
  if (fill === 'none') return undefined;
  if (/^url\s*\(/i.test(fill)) {
    const id = parseUrlRef(fill);
    if (!id) return undefined;
    const resolved: ResolvedGradient | null = resolveFillRef(id, gradients);
    return resolved ?? undefined;
  }
  return fill;
}

// ---------- Document-dimension resolution ----------------------------------

/** Resolve canvas dimensions from the root `<svg>` element. Priority:
 *  viewBox (defines coordinate system) → width/height attributes →
 *  1000×1000 fallback. */
export function resolveSvgDimensions(svg: SVGSVGElement): {
  width: number;
  height: number;
  viewBox: string | null;
} {
  const viewBoxAttr = svg.getAttribute('viewBox');
  if (viewBoxAttr) {
    const parts = viewBoxAttr
      .trim()
      .split(/[\s,]+/)
      .map(Number);
    if (parts.length === 4 && parts.every((n) => !Number.isNaN(n))) {
      // SVG viewBox is `minX minY width height` — we use width/height
      // as canvas dims. minX/minY translation is handled by the
      // walker as part of the root transform.
      const [, , w, h] = parts;
      if (w > 0 && h > 0) return { width: w, height: h, viewBox: viewBoxAttr };
    }
  }
  const widthAttr = svg.getAttribute('width');
  const heightAttr = svg.getAttribute('height');
  const parseUnit = (v: string | null) => {
    if (!v) return null;
    // Strip units (px, pt, em, etc.) — SVG treats them all as px
    // for these attributes when no viewBox is set.
    const n = parseFloat(v);
    return Number.isNaN(n) ? null : n;
  };
  const width = parseUnit(widthAttr) ?? 1000;
  const height = parseUnit(heightAttr) ?? 1000;
  return { width, height, viewBox: null };
}

/** Compute the root transform that maps viewBox coordinates into
 *  document coordinates. When viewBox has non-zero minX/minY, the
 *  content needs to be translated so the top-left of viewBox lands
 *  at (0, 0) in document space.
 *
 *  Pre-PR review note (R1#4): SVG's `preserveAspectRatio` defines
 *  how content scales when the rendered surface's aspect ratio
 *  differs from the viewBox's. The editor sidesteps the question by
 *  using the viewBox's width/height AS the canvas dimensions (see
 *  `resolveSvgDimensions`) — so the rendered surface always matches
 *  the viewBox aspect ratio and preserveAspectRatio is a no-op. If
 *  a future enhancement lets the user override canvas dims at import
 *  time (Photoshop-style "Open at custom size" dialog), the default
 *  `xMidYMid meet` scale-and-center logic would need to land here. */
function viewBoxRootTransform(viewBoxAttr: string | null): Mat2D {
  if (!viewBoxAttr) return IDENTITY_MATRIX;
  const parts = viewBoxAttr
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return IDENTITY_MATRIX;
  const [minX, minY] = parts;
  if (minX === 0 && minY === 0) return IDENTITY_MATRIX;
  return [1, 0, 0, 1, -minX, -minY];
}

// ---------- The walker -----------------------------------------------------

/** Pre-order walk the SVG tree, producing the editor document and
 *  side outputs. Pure — no DOMParser, no rasterizer. The XMLDocument
 *  arg comes from the byte-orchestrator below.
 *
 *  Throws `SvgScriptError` if a `<script>` element is found anywhere
 *  in the document. */
export function buildEditorDocumentFromSvgDoc(
  doc: XMLDocument,
  nextLayerId: () => string,
  svgSource: string,
): SvgImportResult {
  // Reject scripted SVGs outright. The rasterizer also strips
  // `<script>` defensively, but the parser refusing them makes the
  // user-facing intent explicit.
  if (doc.getElementsByTagName('script').length > 0) {
    throw new SvgScriptError();
  }
  // Defense against XXE / billion-laughs entity expansion: if the
  // raw source declares its own entities, refuse. Modern browsers'
  // DOMParser is generally hardened against this but the protection
  // isn't universal across every browser/version. Pattern catches
  // `<!ENTITY` whether or not it appears inside a `<!DOCTYPE [...]>`
  // block (case-insensitive). Pre-PR review finding (R3#4).
  if (/<!ENTITY\b/i.test(svgSource)) {
    throw new SvgEntityDeclarationError();
  }
  const root = doc.documentElement;
  if (!root || root.localName.toLowerCase() !== 'svg') {
    throw new SvgMalformedError('Root element is not <svg>');
  }
  // DOMParser surfaces XML errors as a `<parsererror>` element
  // inside the parsed document. Detect and surface to the user.
  const parserError = doc.getElementsByTagName('parsererror')[0];
  if (parserError) {
    throw new SvgMalformedError(parserError.textContent ?? 'unknown parser error');
  }

  const svgRoot = root as unknown as SVGSVGElement;
  const dims = resolveSvgDimensions(svgRoot);
  const rootTransform = viewBoxRootTransform(dims.viewBox);

  // Extract the root <defs> block as a serialized string. Rasterized
  // fragments that reference `url(#gradient1)` paint servers need
  // the defs in scope to render correctly; the wrapper around each
  // rasterized fragment inlines this block. (Without it, a gradient-
  // filled <rect> rasterizes to opaque black — the paint-server ref
  // doesn't resolve.) Pre-PR review finding (R2#5). The block-level
  // `<defs>` element OR per-element `<linearGradient>` / `<filter>` /
  // `<mask>` etc. all live at this level in well-formed SVGs.
  const defsBlock = extractDefsBlock(svgRoot);

  // Tier 2 Group F SVG round-trip polish (PR-2) — pre-walk every
  // gradient definition in the document so the walker can resolve
  // `url(#id)` fill refs to native `ShapeFillValue` on the fly.
  // Cheap one-pass extraction; the resulting map is read-only.
  const gradientDefs = extractGradientDefs(doc);

  const layers: Record<string, LayerNode> = {};
  const vectorPaths = new Map<string, VectorPath>();
  const rasterDescriptors = new Map<string, SvgRasterDescriptor>();
  const textDescriptors = new Map<string, ParsedSvgTextLayer>();
  /** Maps SVG-element `id` attribute → editor LayerNode id for
   *  path-emitting elements (path / rect / circle / ellipse /
   *  line / polyline / polygon). Used by `parseSvgTextElement`
   *  to resolve `<textPath href="#pathId">` references to the
   *  vector layer the walker imported. Document-order
   *  population — text elements that appear AFTER their bound
   *  path resolve; forward references (text before path) fall
   *  through to raster. (PSD+SVG natives follow-up phase E.) */
  const svgIdToLayerId = new Map<string, string>();
  const layerOriginById: Record<string, SvgNodeOrigin> = {};
  const warnings: SvgImportWarning[] = [];

  // Pre-order DFS index. Increments at every visit so the export
  // path (sub-piece #4) can correlate editor LayerNode.id back to a
  // specific element position in the re-parsed source. Stable across
  // re-parses as long as the document's tree shape is unchanged.
  let svgNodeIndex = 0;
  // Live layer count — abort with a clear error when we'd exceed
  // MAX_SVG_LAYER_COUNT, so a 100,000-element pathological SVG
  // surfaces immediately instead of hanging the editor.
  let emittedLayerCount = 0;

  /** Describe a node for warnings (`<rect#myId>`). */
  function describeNode(el: Element): string {
    const id = el.getAttribute('id');
    return id ? `<${el.localName}#${id}>` : `<${el.localName}>`;
  }

  /** Visit one SVG node. Returns the editor LayerNode.id assigned to
   *  it (or null if the node was skipped). The transform passed in
   *  is the accumulated parent transform (already includes the
   *  root's viewBox translation). `depth` tracks nested-group depth
   *  for the call-stack-overflow guard. */
  function visit(
    el: Element,
    parentTransform: Mat2D,
    parentId: string | null,
    depth: number,
  ): string | null {
    if (depth > MAX_SVG_NESTING_DEPTH) {
      throw new SvgNestingDepthError(depth, MAX_SVG_NESTING_DEPTH);
    }
    const tag = el.localName.toLowerCase();

    // Skip declarative / metadata elements entirely — they don't
    // produce layers but their content may be referenced by other
    // elements (defs / style) or just decorative (title / desc /
    // metadata). Shared with the export walker via `SVG_WALK_SKIP_TAGS`
    // so a future addition has to touch only one place. Pre-PR
    // review finding (R2#1).
    if (SVG_WALK_SKIP_TAGS.has(tag)) {
      return null;
    }

    const presentation = readPresentationAttrs(el as SVGElement, gradientDefs);
    const transformAttr = (el as SVGElement).getAttribute('transform');
    const localTransform = parseSvgTransform(transformAttr);
    const effectiveTransform = multiplyMatrix(parentTransform, localTransform);

    // Group-style elements — recurse children unless the group itself
    // needs rasterization (has filter / mask / clipPath).
    if (tag === 'g' || tag === 'svg') {
      // Nested <svg> elements with their own x/y/width/height/viewBox
      // are technically supported by SVG but their nested coordinate
      // system makes them effectively groups for our purposes; we
      // recurse as a group. Edge cases (e.g. nested viewBox) flatten
      // to raster in v1.
      const isNestedSvgWithViewBox =
        tag === 'svg' && parentId !== null && el.hasAttribute('viewBox');
      if (presentation.needsRaster || isNestedSvgWithViewBox) {
        return emitRasterLayer(
          el,
          effectiveTransform,
          parentId,
          presentation,
          'unsupported-element',
        );
      }
      const groupId = nextLayerId();
      const groupNodeIndex = svgNodeIndex++;
      const childIds: string[] = [];
      const childNodes = Array.from(el.children);
      for (const child of childNodes) {
        const childId = visit(child, effectiveTransform, groupId, depth + 1);
        if (childId) childIds.push(childId);
      }
      if (childIds.length === 0) {
        // Empty group — skip emission rather than littering the
        // editor with phantom containers. Backout the origin index
        // reservation too (would otherwise leave a gap in the index
        // sequence that the export-side re-parse can't reproduce).
        return null;
      }
      enforceLayerBudget();
      layers[groupId] = {
        id: groupId,
        name: (el.getAttribute('id') ?? el.getAttribute('inkscape:label') ?? 'Group').trim(),
        type: 'group',
        visible: presentation.visible,
        opacity: presentation.opacity,
        blendMode: presentation.blendMode,
        parentId,
        // SVG children render in document order (first = behind);
        // editor's `childIds` is top-first (first = front). Reverse.
        childIds: childIds.slice().reverse(),
      };
      layerOriginById[groupId] = { svgNodeIndex: groupNodeIndex };
      emittedLayerCount += 1;
      return groupId;
    }

    // Path-emitting elements.
    if (
      tag === 'path' ||
      tag === 'rect' ||
      tag === 'circle' ||
      tag === 'ellipse' ||
      tag === 'line' ||
      tag === 'polyline' ||
      tag === 'polygon'
    ) {
      if (presentation.needsRaster) {
        const reason: SvgImportWarningReason = el.getAttribute('filter')
          ? 'filter'
          : el.getAttribute('mask')
            ? 'mask'
            : el.getAttribute('clip-path')
              ? 'clip-path'
              : 'gradient-or-pattern';
        return emitRasterLayer(el, effectiveTransform, parentId, presentation, reason);
      }
      const d = pathDataForElement(el, warnings);
      if (d === null || d === '') return null;
      let vectorPath: VectorPath;
      try {
        vectorPath = parseSvgPathData(d);
      } catch (err) {
        warnings.push({
          layerId: null,
          nodeDescription: describeNode(el),
          reason: 'malformed-path',
          message: err instanceof SvgPathParseError ? err.message : 'unknown parse error',
        });
        return null;
      }
      if (vectorPath.subPaths.length === 0) return null;
      const baked = applyMatrixToVectorPath(vectorPath, effectiveTransform);
      const stroke = readStroke(el as SVGElement);
      const fill = readFill(el as SVGElement, gradientDefs);
      enforceLayerBudget();
      const layerId = nextLayerId();
      const myNodeIndex = svgNodeIndex++;
      const builtPath: VectorPath = { ...baked, stroke, fill };
      layers[layerId] = {
        id: layerId,
        name: (el.getAttribute('id') ?? defaultNameForTag(tag)).trim(),
        type: 'vector',
        visible: presentation.visible,
        opacity: presentation.opacity,
        blendMode: presentation.blendMode,
        parentId,
        // Populate LayerNode.path directly — the SVG export's
        // vector branch reads `node.path` (not the side-output
        // `vectorPaths` map). Without this, unedited imported
        // vectors fell through to the raster catch-all and lost
        // their gradient fills on round-trip. Tier 2 Group F SVG
        // round-trip polish (PR-2 phase G).
        path: builtPath,
      };
      vectorPaths.set(layerId, builtPath);
      layerOriginById[layerId] = { svgNodeIndex: myNodeIndex };
      emittedLayerCount += 1;
      // Track the SVG `id` → editor layer id for `<textPath>`
      // resolution (PSD+SVG natives follow-up phase E).
      const svgId = el.getAttribute('id');
      if (svgId) svgIdToLayerId.set(svgId, layerId);
      return layerId;
    }

    // <text> — try native first (Tier 2 Group F SVG round-trip
    // polish, PR-2 phase D). When the element has features the
    // editor's text model can't express (tspan / textPath /
    // gradient fill / non-trivial transform), fall through to the
    // existing raster path. Bare `<tspan>` / `<textPath>` outside
    // a `<text>` parent rarely appear in the wild; if the walker
    // encounters them at top level (no `<text>` wrapper), they
    // rasterize. (Pre-PR review Minor #5 — earlier draft gated
    // native parsing on `presentation.visible !== false`, which
    // silently rasterized hidden text. Vector / image layers
    // carry their visible state through the LayerNode without
    // rasterizing; text should do the same.)
    if (tag === 'text') {
      const textResult = parseSvgTextElement(el, effectiveTransform, {
        svgIdToLayerId,
      });
      if (textResult.ok) {
        enforceLayerBudget();
        const layerId = nextLayerId();
        const myNodeIndex = svgNodeIndex++;
        const textPathBinding = textResult.value.textPathLayerId
          ? { pathLayerId: textResult.value.textPathLayerId }
          : undefined;
        layers[layerId] = {
          id: layerId,
          name: (el.getAttribute('id') ?? 'Text').trim(),
          type: 'text',
          visible: presentation.visible,
          opacity: presentation.opacity,
          blendMode: presentation.blendMode,
          parentId,
          ...(textPathBinding ? { textPath: textPathBinding } : {}),
        };
        textDescriptors.set(layerId, textResult.value);
        layerOriginById[layerId] = { svgNodeIndex: myNodeIndex };
        emittedLayerCount += 1;
        return layerId;
      }
      // Fall through to raster — log the specific reason for
      // future debugging without surfacing it to the user (the
      // `'text'` warning category already covers this case).
    }
    if (tag === 'text' || tag === 'tspan' || tag === 'textpath') {
      return emitRasterLayer(el, effectiveTransform, parentId, presentation, 'text');
    }
    if (tag === 'image') {
      return emitRasterLayer(el, effectiveTransform, parentId, presentation, 'image');
    }
    if (tag === 'use') {
      return emitRasterLayer(el, effectiveTransform, parentId, presentation, 'use-or-symbol');
    }
    if (tag === 'foreignobject') {
      return emitRasterLayer(el, effectiveTransform, parentId, presentation, 'foreign-object');
    }

    // Unknown element — defensive default: warn + skip.
    warnings.push({
      layerId: null,
      nodeDescription: describeNode(el),
      reason: 'unsupported-element',
      message: `Element <${tag}> is not yet supported on import; skipped.`,
    });
    return null;
  }

  function emitRasterLayer(
    el: Element,
    effectiveTransform: Mat2D,
    parentId: string | null,
    presentation: ReturnType<typeof readPresentationAttrs>,
    reason: SvgImportWarningReason,
  ): string {
    enforceLayerBudget();
    const layerId = nextLayerId();
    const myNodeIndex = svgNodeIndex++;
    const bbox = bboxForRasterElement(el, effectiveTransform, dims.width, dims.height);
    // Strip privacy-leak vectors from the serialized fragment BEFORE
    // it lands in the rasterizer / source-stash:
    //   - External <image href="https://..."> URLs (leak user IP)
    //   - <style>...@import url(...)</style> blocks (same)
    //   - Inline `style` attrs with url() fetches
    // Pre-PR review finding (R3#8, R3#9). Stripping at fragment-
    // emission time keeps the rasterizer simple (it can't tell
    // benign from privacy-leak URLs) and preserves the original
    // verbatim in the source-stash for export-time round-trip
    // (the stash holds the ORIGINAL SVG text, separate from this
    // sanitized fragment).
    const fragment = sanitizeFragmentForRaster(new XMLSerializer().serializeToString(el));
    const approximate = !isAxisAlignedMatrix(effectiveTransform);
    layers[layerId] = {
      id: layerId,
      name: (el.getAttribute('id') ?? defaultNameForTag(el.localName)).trim(),
      type: 'image',
      visible: presentation.visible,
      opacity: presentation.opacity,
      blendMode: presentation.blendMode,
      parentId,
    };
    rasterDescriptors.set(layerId, {
      fragment,
      left: bbox.left,
      top: bbox.top,
      width: bbox.width,
      height: bbox.height,
      approximateTransform: approximate,
    });
    layerOriginById[layerId] = { svgNodeIndex: myNodeIndex };
    emittedLayerCount += 1;
    warnings.push({
      layerId,
      nodeDescription: describeNode(el),
      reason,
      message: `<${el.localName}> flattened to raster (preserved in stash for export).`,
    });
    return layerId;
  }

  /** Throw SvgLayerCountError if emitting one more layer would
   *  push past MAX_SVG_LAYER_COUNT. Called BEFORE the layer is
   *  emitted so the caller can abort the walk on overflow. */
  function enforceLayerBudget(): void {
    if (emittedLayerCount >= MAX_SVG_LAYER_COUNT) {
      throw new SvgLayerCountError(emittedLayerCount + 1, MAX_SVG_LAYER_COUNT);
    }
  }

  // Walk the root's children (skipping the root <svg> itself — its
  // attributes determine dims but it's not a layer).
  const rootIds: string[] = [];
  for (const child of Array.from(svgRoot.children)) {
    const id = visit(child, rootTransform, null, 0);
    if (id) rootIds.push(id);
  }

  return {
    document: { rootIds: rootIds.slice().reverse(), layers },
    vectorPaths,
    rasterDescriptors,
    textDescriptors,
    layerOriginById,
    warnings,
    originalWidth: dims.width,
    originalHeight: dims.height,
    defsBlock,
    svgSource,
  };
}

/** Serialize the root SVG's `<defs>` content into a single string
 *  the rasterizer can inline into wrapped fragments. When fragments
 *  reference `url(#id)` paint servers, the defs need to be in scope
 *  for the browser's SVG renderer to resolve them. */
function extractDefsBlock(svgRoot: SVGSVGElement): string {
  const defsEls = Array.from(svgRoot.children).filter(
    (el) => el.localName.toLowerCase() === 'defs',
  );
  if (defsEls.length === 0) return '';
  const serializer = new XMLSerializer();
  return defsEls
    .map((defs) =>
      Array.from(defs.children)
        .map((child) => serializer.serializeToString(child))
        .join(''),
    )
    .join('');
}

/** Strip privacy-leak vectors from an SVG fragment before it ends
 *  up in the rasterizer's `<img>`-loading path. Browsers fetch
 *  external resources (images, CSS @imports) when rendering SVG,
 *  which leaks the user's IP to whatever domain the SVG references.
 *  Stripping at fragment-emission time means the rasterizer (which
 *  can't tell benign URLs from malicious ones) doesn't have to
 *  carry that policy. The ORIGINAL SVG text is preserved verbatim
 *  in the source-stash for export-time round-trip. */
function sanitizeFragmentForRaster(fragment: string): string {
  // 1. External <image href / xlink:href> — replace with a 1×1
  //    transparent PNG data URI so the layout still renders.
  //    Allowed: data:, blob:, and same-origin relative URLs.
  //    `^[\s'"]` matches the attribute value's opening delimiter
  //    (single quote, double quote, or unquoted whitespace).
  const transparentPng =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
  let out = fragment.replace(
    /(<image\b[^>]*?\s(?:xlink:)?href\s*=\s*)(['"])((?:https?:|ftp:|\/\/)[^'"]*)\2/gi,
    `$1$2${transparentPng}$2`,
  );
  // 2. <style>@import ...</style> — strip the entire style block
  //    when it contains @import (CSS imports fetch external
  //    resources). Most SVG <style> blocks declare rules inline;
  //    losing imports is acceptable for the rasterizer path.
  out = out.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, (block) =>
    /@import\b/i.test(block) ? '' : block,
  );
  // 3. Inline `style="...url(...)..."` — strip the url() segment
  //    when it references an external resource. Safer than dropping
  //    the whole style attr (which may carry legit properties).
  out = out.replace(/style\s*=\s*(['"])([^'"]*)\1/gi, (_match, quote: string, value: string) => {
    const sanitized = value.replace(/url\s*\(\s*['"]?(https?:|ftp:|\/\/)[^)]*\)/gi, 'url(#)');
    return `style=${quote}${sanitized}${quote}`;
  });
  return out;
}

/** Compute the path-data string for a path-emitting SVG element.
 *  Returns null when the element's geometry is missing or malformed
 *  (caller emits a warning + skips). */
function pathDataForElement(el: Element, warnings: SvgImportWarning[]): string | null {
  const tag = el.localName.toLowerCase();
  const num = (attr: string, fallback: number) =>
    parseFloat(el.getAttribute(attr) ?? '') || fallback;
  switch (tag) {
    case 'path': {
      const d = el.getAttribute('d');
      return d?.trim() === '' ? null : d;
    }
    case 'rect':
      return rectToPathData({
        x: num('x', 0),
        y: num('y', 0),
        width: num('width', 0),
        height: num('height', 0),
        rx: el.hasAttribute('rx') ? num('rx', 0) : undefined,
        ry: el.hasAttribute('ry') ? num('ry', 0) : undefined,
      });
    case 'circle':
      return circleToPathData({ cx: num('cx', 0), cy: num('cy', 0), r: num('r', 0) });
    case 'ellipse':
      return ellipseToPathData({
        cx: num('cx', 0),
        cy: num('cy', 0),
        rx: num('rx', 0),
        ry: num('ry', 0),
      });
    case 'line':
      return lineToPathData({
        x1: num('x1', 0),
        y1: num('y1', 0),
        x2: num('x2', 0),
        y2: num('y2', 0),
      });
    case 'polyline':
      return pointsToPathData(el.getAttribute('points') ?? '', false);
    case 'polygon':
      return pointsToPathData(el.getAttribute('points') ?? '', true);
    default:
      warnings.push({
        layerId: null,
        nodeDescription: `<${tag}>`,
        reason: 'unsupported-element',
        message: `Path conversion not implemented for <${tag}>.`,
      });
      return null;
  }
}

/** Compute a scene-space axis-aligned bounding box for a raster
 *  element. For elements with explicit x/y/width/height (image,
 *  use), we use those; otherwise fall back to the document's full
 *  canvas (v1 — better bbox detection requires SVG bbox APIs which
 *  happy-dom doesn't ship and which would force this module to be
 *  DOM-bound at runtime). The resulting raster is positioned at the
 *  matrix-transformed (left, top); rotation is approximated by
 *  rendering the axis-aligned bbox of the rotated fragment. */
function bboxForRasterElement(
  el: Element,
  m: Mat2D,
  docWidth: number,
  docHeight: number,
): { left: number; top: number; width: number; height: number } {
  const tag = el.localName.toLowerCase();
  const num = (attr: string, fallback: number) =>
    parseFloat(el.getAttribute(attr) ?? '') || fallback;
  let x = 0;
  let y = 0;
  let w = docWidth;
  let h = docHeight;
  if (tag === 'image' || tag === 'use' || tag === 'foreignobject') {
    x = num('x', 0);
    y = num('y', 0);
    w = num('width', docWidth) || docWidth;
    h = num('height', docHeight) || docHeight;
  } else if (tag === 'text' || tag === 'tspan' || tag === 'textpath') {
    x = num('x', 0);
    y = num('y', 0);
    // Text has no width attr — fall back to remaining doc width.
    w = Math.max(1, docWidth - x);
    h = Math.max(1, docHeight - y);
  }
  // Transform the bbox corners through the matrix; take axis-aligned
  // bounding box of the four transformed corners.
  const corners = [
    applyMatrixToPoint(m, x, y),
    applyMatrixToPoint(m, x + w, y),
    applyMatrixToPoint(m, x + w, y + h),
    applyMatrixToPoint(m, x, y + h),
  ];
  const minX = Math.min(...corners.map((c) => c.x));
  const minY = Math.min(...corners.map((c) => c.y));
  const maxX = Math.max(...corners.map((c) => c.x));
  const maxY = Math.max(...corners.map((c) => c.y));
  return {
    left: minX,
    top: minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

function isAxisAlignedMatrix(m: Mat2D): boolean {
  return m[1] === 0 && m[2] === 0;
}

function defaultNameForTag(tag: string): string {
  switch (tag.toLowerCase()) {
    case 'path':
      return 'Path';
    case 'rect':
      return 'Rectangle';
    case 'circle':
      return 'Circle';
    case 'ellipse':
      return 'Ellipse';
    case 'line':
      return 'Line';
    case 'polyline':
      return 'Polyline';
    case 'polygon':
      return 'Polygon';
    case 'text':
      return 'Text';
    case 'image':
      return 'Image';
    case 'use':
      return 'Use';
    case 'g':
      return 'Group';
    default:
      return tag.charAt(0).toUpperCase() + tag.slice(1);
  }
}

// ---------- Warning aggregation --------------------------------------------

const REASON_LABELS: Record<SvgImportWarningReason, string> = {
  text: 'text',
  'gradient-or-pattern': 'gradient/pattern fill',
  filter: 'filter effect',
  mask: 'mask',
  'clip-path': 'clip path',
  'use-or-symbol': 'symbol reference',
  'foreign-object': 'foreign object',
  image: 'embedded image',
  'malformed-path': 'malformed path',
  'unsupported-blend-mode': 'unsupported blend mode',
  'unsupported-element': 'unsupported element',
};

export interface SvgWarningSummary {
  totalCount: number;
  byReason: { reason: SvgImportWarningReason; count: number; label: string }[];
  title: string;
  description: string;
}

function pluralize(label: string, count: number): string {
  if (count === 1) return label;
  if (label.endsWith('s')) return label;
  return `${label}s`;
}

/** Group raw warnings into a render-ready summary for the toast. */
export function summarizeSvgWarnings(warnings: SvgImportWarning[]): SvgWarningSummary | null {
  if (warnings.length === 0) return null;
  const counts = new Map<SvgImportWarningReason, number>();
  for (const w of warnings) counts.set(w.reason, (counts.get(w.reason) ?? 0) + 1);
  const byReason = (Object.keys(REASON_LABELS) as SvgImportWarningReason[])
    .filter((r) => counts.has(r))
    .map((r) => ({ reason: r, count: counts.get(r)!, label: REASON_LABELS[r] }));
  const totalCount = warnings.length;
  const title = `Imported with ${totalCount} ${pluralize('warning', totalCount)}`;
  const description = byReason
    .map(({ count, label }) => `${count} ${pluralize(label, count)}`)
    .join(', ');
  return { totalCount, byReason, title, description };
}

// ---------- Bytes-in entry point -------------------------------------------

/** Parse SVG bytes into an editor document. Reads the bytes via
 *  DOMParser (works in happy-dom too — the XML mime-type path is
 *  pure XML parsing, no rendering), then walks the resulting
 *  XMLDocument via `buildEditorDocumentFromSvgDoc`.
 *
 *  Throws `SvgScriptError` / `SvgMalformedError` for the documented
 *  parser-level failure modes. */
export function parseSvgToDocument(svgText: string, nextLayerId: () => string): SvgImportResult {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, 'image/svg+xml');
  return buildEditorDocumentFromSvgDoc(doc, nextLayerId, svgText);
}
